/**
 * Guion del workshop del Vault: 9 capítulos que recorren el Vault REAL con datos
 * de demo (lib/vault-demo). La narración es contenido (español rioplatense,
 * voseo), no UI: no pasa por i18n a propósito — la voz en off es una sola.
 *
 * Los ids de los beats nombran los mp3 de `frontend/public/vault-workshop/audio/`;
 * cambiar un texto y correr `node scripts/vault-workshop/synth.mjs` regenera
 * solo ese audio (el manifest guarda el hash de lo dicho).
 *
 * Los ids de las credenciales de demo (`demo-meta-latam`, …) están en
 * lib/vault-demo/demo-vault.ts.
 */
import type { Workshop } from './types';

const PASSPHRASE = 'mate-lluvia-otoño-1974';

export const VAULT_WORKSHOP: Workshop = {
  chapters: [
    {
      id: 'entrar',
      title: 'Así entrás', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Verificación en dos pasos de Google + tu passphrase', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.fresh' }, { go: '/vault' }],
      beats: [
        {
          id: 'entrar-1',
          say: 'Antes de mostrarte el Vault, cómo se entra. No hay un MFA propio: es la verificación en dos pasos de tu cuenta de Google de Winclap, la misma que usás para Gmail.',
          do: [{ waitFor: '@mfa-recheck' }, { wait: 1500 }, { move: '@mfa-activate' }],
        },
        {
          id: 'entrar-2',
          say: 'Si ya la tenés activa, esta pantalla no aparece nunca. Si no, Internal te lo dice acá y te lleva a activarla: son dos minutos.',
          do: [{ wait: 2800 }, { demo: 'mfa.enroll', refresh: false }, { click: '@mfa-recheck' }],
        },
        {
          id: 'entrar-3',
          say: 'La primera vez te pedimos una passphrase propia del Vault. Es un segundo candado: no cifra los datos, protege contra una sesión de Internal abierta en una laptop sin dueño.',
          do: [{ waitFor: '@setup-continue' }, { wait: 4000 }, { click: '@setup-continue' }],
        },
        {
          id: 'entrar-4',
          say: 'Usá una frase larga que solo vos sepas. El Vault mide la fuerza y la compara contra filtraciones conocidas sin enviarla.',
          do: [{ waitFor: '#pp' }, { wait: 600 }, { type: ['#pp', PASSPHRASE] }, { wait: 500 }, { type: ['#pp2', PASSPHRASE], cps: 26 }],
        },
        {
          id: 'entrar-5',
          say: 'Si la perdés, IT la resetea: te avisamos por Slack y queda en el registro. Ninguna credencial se pierde.',
          do: [{ wait: 700 }, { click: '@setup-ack' }, { wait: 1200 }, { click: '@setup-create' }],
        },
        {
          id: 'entrar-6',
          say: 'Listo. El Vault queda desbloqueado quince minutos en esta pestaña; después te pide la passphrase de nuevo.',
          do: [{ waitFor: '@stats' }, { wait: 1200 }, { move: '@lock-toggle' }],
          hold: 800,
        },
      ],
    },
    {
      id: 'credenciales',
      title: 'Tus credenciales', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Solo lo de tus áreas y lo que te compartieron', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault' }],
      beats: [
        {
          id: 'credenciales-1',
          say: 'Esto es lo que ves al entrar: las credenciales de tus áreas del community map y las que te compartieron puntualmente. Nada más.',
          do: [{ waitFor: '@stats' }, { wait: 1200 }, { move: '@col-access' }],
        },
        {
          id: 'credenciales-2',
          say: 'Arriba, cuatro indicadores de salud: cuántas hay, cuántas cuentas compartidas siguen en el censo, cuántas no tienen MFA y cuántas tienen rotación sugerida.',
          do: [
            { move: '[data-tour="stats"] > :nth-child(1)' },
            { wait: 1600 },
            { move: '[data-tour="stats"] > :nth-child(2)' },
            { wait: 1600 },
            { move: '[data-tour="stats"] > :nth-child(3)' },
            { wait: 1600 },
            { move: '[data-tour="stats"] > :nth-child(4)' },
          ],
        },
        {
          id: 'credenciales-3',
          say: 'Con las pestañas filtrás las tuyas, donde sos owner, o las que te compartieron.',
          do: [{ click: '@tab-mine' }, { wait: 1800 }, { click: '@tab-shared' }, { wait: 1800 }, { click: '@tab-all' }],
        },
        {
          id: 'credenciales-4',
          say: 'La columna Mi acceso te dice qué podés hacer con cada una: leer, editar o ser owner. Y el candado de arriba bloquea el Vault cuando te vas de la compu.',
          do: [{ move: '@col-access' }, { wait: 3200 }, { click: '@lock-toggle' }],
          hold: 700,
        },
      ],
    },
    {
      id: 'revelar',
      title: 'Ver una contraseña', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Revelar treinta segundos, copiar, y queda registrado', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.lock' }, { go: '/vault' }],
      beats: [
        {
          id: 'revelar-1',
          say: 'Abrís la credencial que necesitás.',
          do: [{ waitFor: '@secret-demo-meta-latam' }, { wait: 500 }, { click: '@secret-demo-meta-latam' }],
        },
        {
          id: 'revelar-2',
          say: 'Tocás Revelar. Como el Vault está bloqueado, te pide la passphrase.',
          do: [
            { waitFor: '@reveal' },
            { wait: 1200 },
            { click: '@reveal' },
            { waitFor: '#vault-passphrase' },
            { wait: 600 },
            { type: ['#vault-passphrase', PASSPHRASE], cps: 24 },
            { wait: 400 },
            { click: '@unlock-submit' },
          ],
        },
        {
          id: 'revelar-3',
          say: 'La contraseña se muestra treinta segundos y se vuelve a ocultar sola. Lo normal es copiarla y pegarla en la plataforma.',
          do: [{ wait: 2500 }, { click: '@copy-password' }],
        },
        {
          id: 'revelar-4',
          say: 'Cada apertura queda registrada: quién, cuándo y desde qué IP. Y vos lo ves acá, en Actividad reciente. No hace falta anotarla en ningún lado.',
          do: [{ wait: 800 }, { scroll: '@activity' }, { move: '@activity' }],
          hold: 500,
        },
      ],
    },
    {
      id: 'mfa',
      title: 'El código MFA sale del Vault', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Nadie depende del teléfono del líder', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/secrets/demo-meta-latam' }],
      beats: [
        {
          id: 'mfa-1',
          say: 'Ahora, el dolor de hoy. Una cuenta que usan tres o cuatro personas tiene el segundo factor en el Authenticator del líder, en su teléfono. Cada vez que alguien entra, le pide el código por Slack.',
          do: [{ waitFor: '@totp' }, { wait: 1500 }, { move: '@totp' }],
        },
        {
          id: 'mfa-2',
          say: 'Con el Vault eso se termina. Un código de Authenticator es matemática sobre una clave secreta: cualquier app que tenga la misma clave calcula el mismo número.',
          do: [{ wait: 3500 }, { click: '@totp-generate' }],
        },
        {
          id: 'mfa-3',
          say: 'El líder carga esa clave una sola vez en la credencial, y desde ese momento el código de seis dígitos aparece acá, para todos los que tienen acceso. Exactamente el mismo que muestra su Authenticator.',
          do: [{ wait: 4500 }, { move: '@totp-copy' }],
        },
        {
          id: 'mfa-4',
          say: 'Se renueva cada treinta segundos, como en el teléfono. Y cada vez que alguien lo usa, queda en la auditoría.',
          hold: 500,
        },
      ],
    },
    {
      id: 'cargar-mfa',
      title: 'Cómo se carga el MFA', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Con el asistente, paso a paso', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/secrets/demo-accounts-google' }],
      beats: [
        {
          id: 'cargar-1',
          say: 'El Vault no lo detecta solo: el owner lo carga una vez. Esta cuenta compartida todavía dice Sin MFA, y al lado hay un asistente que te lleva paso a paso.',
          do: [{ waitFor: '@mfa-assistant' }, { wait: 1500 }, { move: '@mfa-badge' }, { wait: 1200 }, { click: '@mfa-assistant' }, { waitFor: '@mfa-google' }],
        },
        {
          id: 'cargar-2',
          say: 'Primera pregunta: dónde está hoy el segundo factor de esta cuenta. Lo más común es que esté en el Google Authenticator del teléfono de alguien.',
          do: [{ wait: 3200 }, { click: '@mfa-google' }, { waitFor: '@mfa-have_capture' }],
        },
        {
          id: 'cargar-3',
          say: 'El asistente te dice exactamente qué tocar: Transferir cuentas, Exportar cuentas. Y la regla de oro: destildá todas las cuentas menos la de esta credencial. Las demás son tus claves personales y no tienen que salir del teléfono.',
          do: [{ wait: 800 }, { move: '@mfa-have_capture' }],
        },
        {
          id: 'cargar-4',
          say: 'Le sacás una captura al QR y la arrastrás al chat, o la pegás. Se lee en tu navegador: no se sube a ningún lado ni la ve nadie.',
          do: [{ wait: 1500 }, { drop: ['@mfa-drop', '/vault-workshop/qr-sample.png'] }, { waitFor: '@mfa-save_yes', timeout: 10000 }],
        },
        {
          id: 'cargar-5',
          say: 'Encontró la cuenta y te pregunta si es la de esta credencial. Confirmás, y la clave se guarda cifrada en el Vault.',
          do: [{ wait: 2200 }, { click: '@mfa-save_yes' }, { waitFor: '@totp-code', timeout: 10000 }],
        },
        {
          id: 'cargar-6',
          say: 'Último paso, y el más importante: compará el código que genera el Vault con el que muestra tu teléfono. Si coinciden, listo. Si no, exportaste otra entrada, y el asistente te ayuda a repetirlo.',
          do: [{ wait: 800 }, { move: '@totp-code' }, { wait: 4500 }, { click: '@mfa-match_yes' }, { waitFor: '@mfa-close' }],
        },
        {
          id: 'cargar-7',
          say: 'Desde ahora, todos los que tienen acceso ven el código acá, sin depender de un teléfono. Y si el segundo factor está en otra app o la cuenta todavía no tiene, el mismo asistente te guía por la plataforma, paso a paso.',
          do: [{ wait: 4500 }, { click: '@mfa-close' }],
          hold: 600,
        },
      ],
    },
    {
      id: 'compartir',
      title: 'Compartir acceso y grupos', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Tres niveles: leer, editar, owner', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/secrets/demo-meta-latam' }],
      beats: [
        {
          id: 'compartir-1',
          say: 'Cada credencial tiene tres niveles: leer, editar y owner. Solo el owner otorga y quita accesos, a una persona o a un grupo entero.',
          do: [{ waitFor: '@grants' }, { wait: 800 }, { scroll: '@grants' }, { move: '@grants' }],
        },
        {
          id: 'compartir-2',
          say: 'Dar acceso es buscar a la persona en el community map y agregar. Si compartís con el grupo de un área, entra automáticamente quien se sume a esa área.',
          do: [{ wait: 600 }, { click: '@grant-pick' }, { waitFor: '@member-search' }, { wait: 500 }, { type: ['@member-search', 'luc'], cps: 8 }, { wait: 900 }, { click: '@member-row' }, { wait: 700 }, { click: '@grant-add' }],
        },
        {
          id: 'compartir-3',
          say: 'Un límite importante: ni siquiera un admin de Internal puede darse acceso a lo que no le compartieron. Si necesitás una credencial, pedísela al owner, no a IT.',
          do: [{ wait: 3500 }, { move: 'a[href="/vault/groups"]', press: true, timeout: 1500 }, { go: '/vault/groups' }],
        },
        {
          id: 'compartir-4',
          say: 'Los grupos por área salen solos del community map. Y cualquiera puede crear un grupo ad hoc para un proyecto o un cliente, con roles de manager, editor o reader.',
          do: [{ waitFor: '@groups-area' }, { wait: 500 }, { move: '@groups-area' }, { wait: 3500 }, { scroll: '@groups-adhoc' }, { move: '@groups-adhoc' }],
        },
        {
          id: 'compartir-5',
          say: 'Sacar a alguien de un grupo marca para rotar todo lo que ese grupo alcanzaba. Quien pudo ver una contraseña, pudo copiarla.',
          hold: 500,
        },
      ],
    },
    {
      id: 'rotar',
      title: 'Rotar una contraseña', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Revocar implica rotar', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/secrets/demo-semrush' }],
      beats: [
        {
          id: 'rotar-1',
          say: 'Una credencial queda con rotación sugerida sola: cuando alguien pierde el acceso, o cuando pasan noventa días sin rotarla. El owner recibe un mensaje directo; rotarla es una sugerencia, no una obligación.',
          do: [{ waitFor: '@needs-rotation' }, { wait: 1200 }, { move: '@needs-rotation' }],
        },
        {
          id: 'rotar-2',
          say: 'Rotar es cambiar la contraseña en la plataforma y guardarla acá. El Vault te propone una fuerte.',
          do: [
            { wait: 1200 },
            { click: '@rotate' },
            { waitFor: '@rotate-generate' },
            { wait: 800 },
            { click: '@rotate-generate' },
            { wait: 1400 },
            { click: '@rotate-ack' },
            { wait: 800 },
            { click: '@rotate-submit' },
          ],
        },
        {
          id: 'rotar-3',
          say: 'La versión anterior queda en el historial, visible solo para quien tenía acceso en ese momento. Y la marca de rotación desaparece.',
          do: [{ waitFor: '@history' }, { wait: 1000 }, { scroll: '@history' }, { move: '@history' }],
          hold: 500,
        },
      ],
    },
    {
      id: 'link',
      title: 'Compartir con alguien de afuera', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Link temporal, nunca Slack ni mail', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/secrets/demo-meta-latam' }],
      beats: [
        {
          id: 'link-1',
          say: 'Para pasarle una credencial a alguien que no usa Internal, un freelancer, alguien del cliente, no la pegues en Slack ni en un mail. Creá un link temporal.',
          do: [{ waitFor: '@share' }, { wait: 2500 }, { click: '@share' }],
        },
        {
          id: 'link-2',
          say: 'Vence en una, veinticuatro o setenta y dos horas. Puede ser de un solo uso, y si sos owner, puede incluir el código MFA.',
          do: [{ waitFor: '@share-create' }, { wait: 800 }, { move: '@share-expires' }, { wait: 2200 }, { move: '@share-onetime' }, { wait: 1800 }, { click: '@share-create' }],
        },
        {
          id: 'link-3',
          say: 'Quien lo abre no necesita Internal, y cada apertura queda registrada con su IP. Pegarlo en Slack no lo gasta: el reveal es un clic dentro de la página.',
          do: [{ waitFor: '@share-url' }, { wait: 800 }, { move: '@share-url' }, { wait: 3000 }, { click: '@share-copy' }],
        },
        {
          id: 'link-4',
          say: 'Si un link llegó a quien no debía, lo revocás desde la misma pantalla.',
          do: [{ wait: 1200 }, { move: '@share-existing' }, { wait: 1800 }, { key: 'Escape' }],
          hold: 400,
        },
      ],
    },
    {
      id: 'auditoria',
      title: 'Todo queda registrado', // i18n-ok: contenido del workshop (narración en español, no UI)
      subtitle: 'Y las cuatro reglas de oro', // i18n-ok: contenido del workshop (narración en español, no UI)
      setup: [{ demo: 'access.ready' }, { demo: 'session.unlock' }, { go: '/vault/audit' }],
      beats: [
        {
          id: 'auditoria-1',
          say: 'En Auditoría ves tu propia actividad: qué revelaste, qué copiaste, qué compartiste, cuándo y desde dónde. IT ve el registro completo.',
          do: [{ waitFor: '@my-activity' }, { wait: 1500 }, { move: '@my-activity' }],
        },
        {
          id: 'auditoria-2',
          say: 'Es de solo lectura y se replica a un almacenamiento inmutable: ni un admin de Internal puede alterarlo. Ver una credencial no es un problema. Lo que buscamos es poder atribuir cada acceso a una persona.',
        },
        {
          id: 'auditoria-3',
          say: 'Para cerrar, cuatro reglas. Uno: una contraseña compartida vive solo en el Vault. Dos: para pasarla, link temporal, nunca Slack ni mail. Tres: si te sacan de un grupo, no te sorprenda que la rotemos. Cuatro: si sos owner, cargá el MFA y rotá cuando el Vault te lo pida.',
          overlay: 'rules',
        },
        {
          id: 'auditoria-4',
          say: 'Eso es todo. Entrá a internal punto winclap punto com, barra vault, y pedile acceso al owner de lo que necesites. Gracias.',
          hold: 800,
        },
      ],
    },
  ],
  rules: [
    { title: 'La contraseña vive en el Vault.', body: 'Nada de sheets, notas ni chats.' }, // i18n-ok: contenido del workshop (narración en español, no UI)
    { title: 'Para pasarla, link temporal.', body: 'Un solo uso, vence solo, queda registrado.' }, // i18n-ok: contenido del workshop (narración en español, no UI)
    { title: 'Perdiste el acceso ⇒ se rota.', body: 'Quien pudo verla pudo copiarla.' }, // i18n-ok: contenido del workshop (narración en español, no UI)
    { title: 'Owner: cargá el MFA y rotá cuando te lo pida.', body: 'Ni un admin puede darse acceso solo.' }, // i18n-ok: contenido del workshop (narración en español, no UI)
  ],
};

/** Total de beats (para la barra de progreso). */
export const workshopBeatCount = (w: Workshop) => w.chapters.reduce((n, c) => n + c.beats.length, 0);
